import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Checkbox,
    FormControlLabel,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ListIcon from '@mui/icons-material/List';
import { toast } from 'react-toastify';

/**
 * AddToListModal Component
 * Modal for adding an application to one or more subscription lists
 */
const AddToListModal = ({
    open,
    onClose,
    applicationName,
    applicationId,
    existingLists, // Array of { id, name, appCount, hasApplication }
    onAddToLists,
    onRemoveFromLists,
}) => {
    const [selectedLists, setSelectedLists] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Initialize selected state based on which lists already contain the app
    useEffect(() => {
        if (open && existingLists) {
            const initialState = {};
            existingLists.forEach((list) => {
                initialState[list.id] = list.hasApplication || false;
            });
            setSelectedLists(initialState);
        }
    }, [open, existingLists]);

    const handleClose = () => {
        setSelectedLists({});
        onClose();
    };

    const handleToggleList = (listId) => {
        setSelectedLists((prev) => ({
            ...prev,
            [listId]: !prev[listId],
        }));
    };

    const handleSave = async () => {
        setIsLoading(true);

        try {
            // Determine which lists to add to and which to remove from
            const listsToAdd = [];
            const listsToRemove = [];

            existingLists.forEach((list) => {
                const wasSelected = list.hasApplication;
                const isNowSelected = selectedLists[list.id];

                if (!wasSelected && isNowSelected) {
                    listsToAdd.push(list.id);
                } else if (wasSelected && !isNowSelected) {
                    listsToRemove.push(list.id);
                }
            });

            // Execute add operations
            if (listsToAdd.length > 0 && onAddToLists) {
                await onAddToLists(listsToAdd);
            }

            // Execute remove operations
            if (listsToRemove.length > 0 && onRemoveFromLists) {
                await onRemoveFromLists(listsToRemove);
            }

            if (listsToAdd.length > 0 || listsToRemove.length > 0) {
                toast.success('List membership updated successfully', {
                    position: 'top-center',
                    autoClose: 3000,
                });
            }

            handleClose();
        } catch (error) {
            toast.error(error.message || 'Failed to update list membership', {
                position: 'top-center',
                autoClose: 3000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const hasChanges = () => {
        if (!existingLists) return false;
        
        return existingLists.some((list) => {
            const wasSelected = list.hasApplication;
            const isNowSelected = selectedLists[list.id];
            return wasSelected !== isNowSelected;
        });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '8px',
                },
            }}
        >
            <DialogTitle
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    paddingBottom: '16px',
                }}
            >
                <span style={{ fontWeight: 500, fontSize: '18px' }}>
                    Add to Subscription List
                </span>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent style={{ padding: '24px' }}>
                {/* Application Info */}
                <div
                    style={{
                        backgroundColor: '#f5f5f5',
                        padding: '12px 16px',
                        borderRadius: '4px',
                        marginBottom: '20px',
                    }}
                >
                    <span style={{ fontWeight: 500 }}>{applicationName}</span>
                    {applicationId && (
                        <span style={{ color: '#666', marginLeft: '8px' }}>
                            ({applicationId})
                        </span>
                    )}
                </div>

                {/* Lists Selection */}
                <div style={{ marginBottom: '8px' }}>
                    <label
                        style={{
                            display: 'block',
                            marginBottom: '12px',
                            fontWeight: 500,
                            fontSize: '14px',
                        }}
                    >
                        Select Lists
                    </label>

                    {existingLists && existingLists.length > 0 ? (
                        <div
                            style={{
                                maxHeight: '300px',
                                overflowY: 'auto',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                            }}
                        >
                            {existingLists.map((list) => (
                                <div
                                    key={list.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #f0f0f0',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                    }}
                                    onClick={() => handleToggleList(list.id)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f9f9f9';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <Checkbox
                                        checked={selectedLists[list.id] || false}
                                        onChange={() => handleToggleList(list.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            color: '#141414',
                                            '&.Mui-checked': {
                                                color: '#42b0d5',
                                            },
                                        }}
                                    />
                                    <ListIcon
                                        style={{
                                            color: '#42b0d5',
                                            marginRight: '12px',
                                        }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 500 }}>{list.name}</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            {list.appCount} application{list.appCount !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    {list.hasApplication && (
                                        <span
                                            style={{
                                                fontSize: '11px',
                                                color: '#4caf50',
                                                backgroundColor: '#e8f5e9',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            Already added
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: '#666',
                                border: '1px dashed #e0e0e0',
                                borderRadius: '4px',
                            }}
                        >
                            <ListIcon
                                style={{
                                    fontSize: '48px',
                                    color: '#ccc',
                                    marginBottom: '12px',
                                }}
                            />
                            <p>No subscription lists available.</p>
                            <p style={{ fontSize: '12px' }}>
                                Create a subscription list first to add applications.
                            </p>
                        </div>
                    )}
                </div>
            </DialogContent>

            <DialogActions
                style={{
                    padding: '16px 24px',
                    borderTop: '1px solid #e0e0e0',
                }}
            >
                <button
                    onClick={handleClose}
                    className="animated-search-button"
                    style={{
                        backgroundColor: '#f5f5f5',
                        color: '#333',
                    }}
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="animated-search-button"
                    disabled={isLoading || !hasChanges()}
                    style={{
                        backgroundColor: hasChanges() ? '#42b0d5' : '#ccc',
                    }}
                >
                    {isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        'Save Changes'
                    )}
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default AddToListModal;
